import { createContext, useCallback, useContext, useState } from 'react';

import { Permission } from '@/api';
import { useLocalStorageState } from '@/services/localStorage';
import useDebouncedState from '@/utils/useDebouncedState';

import { type RoleListScopeKey } from '../scope-labels';

type FiltersState = {
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  scopes: {
    scopes: RoleListScopeKey[];
    setScopes: (scopes: RoleListScopeKey[]) => void;
  };
  permissions: {
    permissions: Permission[];
    setPermissions: (permissions: Permission[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = (): FiltersState => {
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), []);

  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const [scopes, setScopes] = useLocalStorageState<RoleListScopeKey[]>(
    'settings:organization:roles:filters:scopes',
    []
  );

  const [permissions, setPermissions] = useLocalStorageState<Permission[]>(
    'settings:organization:roles:filters:permissions',
    []
  );

  return {
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    scopes: {
      scopes,
      setScopes: (next: RoleListScopeKey[]) => {
        setScopes(next);
        resetPage();
      },
    },
    permissions: {
      permissions,
      setPermissions: (next: Permission[]) => {
        setPermissions(next);
        resetPage();
      },
    },
    activeFiltersCount: scopes.length + permissions.length,
    clearFilters: () => {
      setScopes([]);
      setPermissions([]);
      resetPage();
    },
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
